import React from 'react';
import { useTranslation } from 'react-i18next';
import { User, UserLevel, Level } from '../../../types';
import { Button } from '../../../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { getMediaUrl } from '../../../utils/media';

interface ProfileHeaderProps {
    user: User | null;
    userLevel: UserLevel | null;
    levels: Level[];
    onEditProfile: () => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ user, userLevel, levels, onEditProfile }) => {
    const { t, i18n } = useTranslation(['profile', 'common']);

    if (!user) return null;

    const currentLevel = userLevel?.level || 0;
    const nextLevel = levels.find(l => l.level_number === currentLevel + 1);
    const pointsToNext = userLevel?.points_to_next || (nextLevel ? nextLevel.required_points - (userLevel?.total_points || 0) : 0);

    return (
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
                <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                    <AvatarImage src={getMediaUrl(user.avatar)} alt={user.username} />
                    <AvatarFallback className="text-2xl bg-primary-100 text-primary-600">
                        {(user.full_name || user.username || 'U')[0].toUpperCase()}
                    </AvatarFallback>
                </Avatar>

                <div className="flex-1 space-y-2">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
                                {user.full_name || user.username}
                            </h1>
                            <div className="flex items-center gap-2 mt-1 text-gray-500 text-sm">
                                <span>@{user.username}</span>
                                {user.role === 'admin' && (
                                    <Badge variant="secondary" className="bg-primary-100 text-primary-700">
                                        Admin
                                    </Badge>
                                )}
                            </div>
                        </div>

                        <Button
                            onClick={onEditProfile}
                            variant="outline"
                            className="gap-2"
                        >
                            <span>✏️</span>
                            {t('sidebar.editProfile')}
                        </Button>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-4">
                        <div className="flex items-center gap-1">
                            <span>📅</span>
                            <span>{t('sidebar.joined', { date: new Date(user.created_at).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }) })}</span>
                        </div>
                        {user.location && (
                            <div className="flex items-center gap-1">
                                <span>📍</span>
                                <span>{user.location}</span>
                            </div>
                        )}
                    </div>

                    {/* Level Progress */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-primary-700">
                                {t('sidebar.level', { level: currentLevel })}
                            </span>
                            <span className="text-xs text-gray-500">
                                {pointsToNext > 0
                                    ? t('sidebar.pointsToNext', { points: pointsToNext })
                                    : t('sidebar.maxLevel', { defaultValue: 'Max Level' })
                                }
                            </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-500"
                                style={{ width: `${Math.min(100, (userLevel?.total_points || 0) / (nextLevel?.required_points || (userLevel?.total_points || 1)) * 100)}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {user.bio && (
                <div className="mt-6 p-4 bg-gray-50 rounded-xl text-gray-600 italic">
                    {user.bio}
                </div>
            )}
        </div>
    );
};
